/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import PropTypes from 'prop-types'
import {getAppOrigin} from '@salesforce/pwa-kit-react-sdk/utils/url'
import logger from '@salesforce/retail-react-app/app/utils/logger-instance'
import fetch from 'cross-fetch'
import {getMrtApiBasepath} from '../ssr/helpers'
import {getPathWithLocale} from '@salesforce/retail-react-app/app/utils/url'
import Cookies from 'js-cookie'

/**
 * Dynamically import the translations/messages for a given locale
 * @param {string} locale
 * @param {string} origin
 * @returns {Promise<Object>} The messages (compiled in AST format) in the given locale.
 *      If the translation file is not found, return an empty object (so react-intl would fall back to the inline messages)
 */
export const fetchTranslations = async (locale) => {
    try {
        const endpoint = getAppOrigin() + getMrtApiBasepath('/get-labels?locale=' + locale)
        const response = await fetch(endpoint)

        if (!response.ok) {
            throw new Error(
                `Failed to fetch . Received the response: ${response.status} ${response.statusText}`,
            )
        }

        return await response.json()
    } catch (err) {
        logger.error(
            'Translation not found. Loading empty messages, so that react-intl would fall back to the inline default messages',
            {namespace: 'utils.fetchTranslations', additionalProperties: {error: err}},
        )
        return {}
    }
}

/**
 * Determine the target locale by comparing the users' preferred locales with the app's supported locales
 * @param {Object} options
 * @param {function} [options.getUserPreferredLocales] - Identify what set of locales the user prefers
 * @param {object} [options.l10nConfig] - The current site's l10n configuration object
 * @returns {string} The target locale's short code
 */
export const getTargetLocale = ({getUserPreferredLocales, l10nConfig = {}} = {}) => {
    const userPreferredLocales = getUserPreferredLocales ? getUserPreferredLocales() : []
    const supportedLocales = l10nConfig.supportedLocales.map((locale) => locale.alias)
    const defaultLocale = l10nConfig.defaultLocale

    const targetLocale = determineTargetLocale(
        userPreferredLocales,
        supportedLocales,
        defaultLocale,
    )
    return targetLocale
}

/**
 * Decide which locale to load
 * @private
 * @param {string[]} preferredLocales - All locales that the user prefers
 * @param {string[]} supportedLocales - All locales that your app supports
 * @param {string} fallbackLocale - App's default locale
 * @returns {string} The target locale if there's a match. Otherwise, returns `fallbackLocale`.
 */
export const determineTargetLocale = (preferredLocales, supportedLocales, fallbackLocale) => {
    const targetLocale = preferredLocales.filter((locale) => supportedLocales.includes(locale))[0]
    return targetLocale || fallbackLocale
}

// Creates the url for the correct locale in the country-selector-banner and redirects the user
export const handleLocale = (event, locale, buildUrl, cdnValue = null) => {
    event.preventDefault()
    if (locale.url === undefined) return

    if (cdnValue) {
        Cookies.set('dlg-country', cdnValue, {expires: 30})
    }

    //if the url start with / it needs to be removed for the buildUrl to work
    let {url} = locale
    const isSpecialChar = new RegExp('^/', 'i')
    if (isSpecialChar.test(url)) {
        url = url.slice(1)
    }
    const newUrl = getPathWithLocale(url, buildUrl, {
        disallowParams: ['refine'],
        location: {
            pathname: '/',
            search: '',
        },
    })
    window.location = newUrl
}

export const MESSAGE_PROPTYPE = PropTypes.shape({
    // NOTE: defaultMessage is typically written as a string
    // but its value can be an array when it comes from the compiled AST version
    defaultMessage: PropTypes.oneOfType([PropTypes.string, PropTypes.array]).isRequired,
    id: PropTypes.string,
})
